import { useCallback } from 'react';

import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { paths } from 'src/routes/paths';
import { useParams, useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { Contact } from 'src/services/contacts/contacts.types';
import { IOrgCandidate } from 'src/services/candidates/candidates.types';
import { ListItem, ListEntityType } from 'src/services/lists/lists.types';
import { ICompanyProfile } from 'src/services/connections/connections.types';

import { usePopover } from 'src/components/custom-popover';

import ListItemCompanyTableRow from 'src/sections/lists/detail/rows/list-company-table-row';
import { ListItemContactTableRow } from 'src/sections/lists/detail/rows/list-contact-table-row';
import { ListItemCandidateTableRow } from 'src/sections/lists/detail/rows/list-candidate-table-row';

import { TenantType } from 'src/types/enums';


// ----------------------------------------------------------------------
type Props = {
  row: ListItem,
  entityType: ListEntityType;
  selected?: boolean;
  onSelectRow?: VoidFunction;
  onRemoveItem?: VoidFunction;
};

export default function ListsItemTableRow({
  row,
  entityType,
  selected,
  onSelectRow,
  onRemoveItem
}: Props) {

  const theme = useTheme();

  const tenantType = useOrgTenant();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const popover = usePopover();

  const { t } = useTranslate();

  const router = useRouter();

  const dispatch = useAppDispatch();

  const { target_tenant } = useParams();

  const getViewDetailPath = useCallback((data: ListItem) => {
    switch (entityType) {
      case ListEntityType.COMPANY:
        return paths.dashboard.companies.detail(data.id, tenantType, row.company_profile?.company_type as TenantType);
      case ListEntityType.CONTACT:
        return paths.dashboard.contacts.detail(data.id);
      case ListEntityType.CANDIDATE:
        return paths.dashboard.candidates.detail(data.id);
      default:
        return '';
    }
  }, [tenantType, entityType, row]);

  const viewDetail = useCallback((data: ListItem) => {
    const path = getViewDetailPath(data);
    if (path) {
      router.push(path);
    }
  }, [router, getViewDetailPath]);

  const getEntityRow = useCallback((data: ListItem) => {
    switch (entityType) {
      case ListEntityType.COMPANY:
        return (
          <ListItemCompanyTableRow
            row={data.company_profile as ICompanyProfile}
            selected={selected}
            onSelectRow={onSelectRow}
            onRemoveItem={onRemoveItem}
          />
        );
      case ListEntityType.CONTACT:
        return (
          <ListItemContactTableRow
            row={data.contact as Contact}
            selected={selected}
            onSelectRow={onSelectRow}
            onRemoveItem={onRemoveItem}
          />
        );
      case ListEntityType.CANDIDATE:
        return (
          <ListItemCandidateTableRow
            row={data.organization_candidate as IOrgCandidate}
            selected={selected}
            onSelectRow={onSelectRow}
            onRemoveItem={onRemoveItem}
          />
        );
      default:
        return null;
    }
  }, [entityType, selected, onSelectRow, onRemoveItem]);

  return (
    <>
      {getEntityRow(row)}
    </>
  );
}